import './assets/css/init.css'
import 'element-plus/dist/index.css'
import './assets/css/common.scss'

import ElementPlus from 'element-plus'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import router from './router'
import registerCpns from './components'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import { createApp } from 'vue'
import store from './store'
import App from './App.vue'
import {parseUrl} from '@/utils/func'
import { get } from '@/utils/request'
import { getThirdPartyUserDetail } from '@/api/base'

const paramsObj=parseUrl();
if(paramsObj && paramsObj.token){
  localStorage.setItem('Token',paramsObj.token);
}

// 从 URL 读取 code（兼容普通 query 与 hash 路由 #/path?code=）
function getCodeFromUrl() {
  let queryStr = window.location.search || ''
  if (!queryStr) {
    const hash = window.location.hash || ''
    const qIdx = hash.indexOf('?')
    if (qIdx !== -1) queryStr = hash.slice(qIdx + 1)
  }
  const params = new URLSearchParams(queryStr)
  let code = params.get('code') || ''
  if (code.endsWith('#')) code = code.slice(0, -1)
  return code
}

// 换取 token 后清除 URL 上的 code，避免刷新重复使用一次性 code
function removeCodeFromUrl() {
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    const hash = url.hash || ''
    const qIdx = hash.indexOf('?')
    if (qIdx !== -1) {
      const base = hash.slice(0, qIdx)
      const hp = new URLSearchParams(hash.slice(qIdx + 1))
      hp.delete('code')
      const nq = hp.toString()
      url.hash = nq ? `${base}?${nq}` : base
    }
    window.history.replaceState({}, '', url.toString())
  } catch (e) {
    // ignore
  }
}

const CODE_USED_KEY = 'authCodeUsed'

// 记录 code 是否已请求过，避免重复调用一次性 code 接口
function isCodeUsed(code) {
  try {
    return sessionStorage.getItem(CODE_USED_KEY) === code
  } catch (e) {
    return false
  }
}

function markCodeUsed(code) {
  try {
    sessionStorage.setItem(CODE_USED_KEY, code)
  } catch (e) {
    // ignore
  }
}

// 通过 code 换取 token，存入 localStorage.Token，后续请求拦截器自动携带
async function fetchTokenByCode(code) {
  // 已请求过的 code 不再重复请求，直接清除 URL 上的 code
  if (isCodeUsed(code)) {
    console.info('[auth] code 已请求过，跳过重复请求')
    removeCodeFromUrl()
    return false
  }
  // 请求前先标记，避免请求期间刷新导致重复调用
  markCodeUsed(code)
  let res
  try {
    res = await get('/auth/token-by-code', { code })
  } catch (err) {
    // 请求失败也清除 URL 上的 code，避免刷新重复请求无效 code
    removeCodeFromUrl()
    throw err
  }
  // 兼容多种返回结构：直接字符串、{data: token}、{data: {token}}、{data: {accessToken}}
  let token = ''
  let refreshToken = ''
  let info = null  // 完整 token 信息（accessToken/refreshToken/expiresIn/roles/userId 等）
  if (typeof res === 'string') {
    token = res
  } else if (res && typeof res === 'object') {
    const data = res.data
    if (typeof data === 'string') {
      token = data
    } else if (data && typeof data === 'object') {
      info = data
      token = data.accessToken || data.token || ''
      refreshToken = data.refreshToken || data.refresh_token || ''
    }
    if (!refreshToken) refreshToken = res.refreshToken || res.refresh_token || ''
  }
  if (!token) {
    removeCodeFromUrl()
    throw new Error('token-by-code 响应无 token 字段')
  }
  localStorage.setItem('Token', token)
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
  if (info) {
    const roles = info.roles || []
    localStorage.setItem('roles', JSON.stringify(roles))
    localStorage.setItem('userId', info.userId || '')
    localStorage.setItem('username', info.username || '')
    // 单角色默认选中第一个；多角色清空，由 Home 弹窗选择
    if (roles.length === 1) {
      const r = roles[0]
      const norm = typeof r === 'string' ? { id: r, name: r } : { id: r.id, name: r.name || r.id }
      localStorage.setItem('selectedRole', JSON.stringify(norm))
    } else {
      localStorage.removeItem('selectedRole')
    }
  }
  removeCodeFromUrl()
  // 获取用户详细信息存本地
  if (info?.userId) {
    try {
      const userRes = await getThirdPartyUserDetail(info.userId)
      localStorage.setItem('userInfo', JSON.stringify(userRes))
      if (userRes?.data?.name) localStorage.setItem('name', userRes.data.name)
    } catch (e) {
      console.error('[auth] 获取用户信息失败:', e.message || e)
    }
  }
  console.info('[auth] 通过 code 获取 token 成功')
  return true
}

const app = createApp(App)
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(router);
app.use(store);
app.use(registerCpns);
app.use(ElementPlus,{
  locale: zhCn
})

// 启动：URL 带 code 时先换 token 再挂载，保证后续请求都携带 token；无 code 直接挂载
;(async function bootstrap() {
  const code = getCodeFromUrl()
  if (code) {
    try {
      await fetchTokenByCode(code)
    } catch (err) {
      console.error('[auth] 通过 code 获取 token 失败：', err.message || err)
      // 跳 SSO 登录重新获取 code
      window.location.href = window.getSsoLoginURL()
      return  // 不挂载当前页
    }
  }
  app.mount('#app')
})()
